import { z } from "zod";

/** RPC metrics: null / undefined / "" → 0; invalid values → 0 */
export const analyticsMetricSchema = z.preprocess(
  (val) => (val == null || val === "" ? 0 : val),
  z.coerce.number().catch(0),
);

export const analyticsKpiSchema = z.object({
  clicks: analyticsMetricSchema,
  uniques: analyticsMetricSchema,
  conversions: analyticsMetricSchema,
  revenue: analyticsMetricSchema,
  uepc: analyticsMetricSchema,
});

export const analyticsChartDataPointSchema = z.object({
  time_label: z.string().default(""),
  clicks: analyticsMetricSchema,
  uniques: analyticsMetricSchema,
  conversions: analyticsMetricSchema,
  revenue: analyticsMetricSchema,
});

export const analyticsBreakdownRowSchema = z.object({
  name: z.preprocess(
    (val) => (val == null || val === "" ? "Unknown" : val),
    z.string(),
  ),
  clicks: analyticsMetricSchema,
  uniques: analyticsMetricSchema,
  conversions: analyticsMetricSchema,
  revenue: analyticsMetricSchema,
});

export const analyticsRecentClickSchema = z.object({
  datetime: z.string().default(""),
  campaign: z.string().default(""),
  os: z.string().default(""),
  browser: z.string().default(""),
  ip: z.string().default(""),
  destination: z.string().default(""),
});

/** `get_analytics_dashboard` RPC payload (`Returns: Json`). */
export const analyticsDashboardResponseSchema = z.object({
  kpi: analyticsKpiSchema.default({
    clicks: 0,
    uniques: 0,
    conversions: 0,
    revenue: 0,
    uepc: 0,
  }),
  chart: z.array(analyticsChartDataPointSchema).default([]),
  top_campaigns: z.array(analyticsBreakdownRowSchema).default([]),
  top_landings: z.array(analyticsBreakdownRowSchema).default([]),
  top_sources: z.array(analyticsBreakdownRowSchema).default([]),
  recent_clicks: z.array(analyticsRecentClickSchema).default([]),
});

export type AnalyticsKPI = z.infer<typeof analyticsKpiSchema>;
export type AnalyticsChartDataPoint = z.infer<
  typeof analyticsChartDataPointSchema
>;
export type AnalyticsBreakdownRow = z.infer<typeof analyticsBreakdownRowSchema>;
export type AnalyticsRecentClick = z.infer<typeof analyticsRecentClickSchema>;
export type AnalyticsDashboardData = z.infer<
  typeof analyticsDashboardResponseSchema
>;

export const EMPTY_ANALYTICS_DASHBOARD: AnalyticsDashboardData = {
  kpi: {
    clicks: 0,
    uniques: 0,
    conversions: 0,
    revenue: 0,
    uepc: 0,
  },
  chart: [],
  top_campaigns: [],
  top_landings: [],
  top_sources: [],
  recent_clicks: [],
};
